import { useState, useEffect } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { 
  Flame, 
  Trophy, 
  Target, 
  Droplets, 
  TrendingUp,
  ChevronRight,
  Award,
  Calendar,
  Check,
  X
} from 'lucide-react';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';

const NutritionHabits = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAchievements, setShowAchievements] = useState(false);
  const [allAchievements, setAllAchievements] = useState([]);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const response = await axios.get(`${API}/nutrition/habits/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Error loading habit stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadAchievements = async () => {
    try {
      const response = await axios.get(`${API}/nutrition/habits/achievements`);
      setAllAchievements(response.data.achievements || []);
      setShowAchievements(true);
    } catch (error) {
      toast.error('Failed to load achievements');
    }
  };

  const getStreakColor = (streak) => {
    if (streak >= 7) return 'text-green-400';
    if (streak >= 3) return 'text-yellow-400';
    return 'text-stone-400';
  };

  const getStreakFlame = (streak) => {
    if (streak >= 7) return '🔥';
    if (streak >= 3) return '✨';
    return '';
  };

  if (loading) {
    return (
      <Card className="bg-gradient-to-br from-purple-900/40 to-pink-900/40 border-purple-500/30 p-4">
        <div className="animate-pulse">
          <div className="h-5 bg-purple-500/20 rounded w-1/3 mb-4"></div>
          <div className="grid grid-cols-4 gap-2">
            {[1,2,3,4].map(i => (
              <div key={i} className="h-20 bg-purple-500/20 rounded"></div>
            ))}
          </div>
        </div>
      </Card>
    );
  }

  if (!stats) return null;

  return (
    <Card className="bg-gradient-to-br from-purple-900/40 to-pink-900/40 border-purple-500/30 p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Flame className="w-5 h-5 text-orange-400" />
          <h3 className="text-lg font-bold text-white">Weekly Habits</h3>
        </div>
        <Button
          size="sm"
          variant="ghost"
          onClick={loadAchievements}
          className="text-purple-400 hover:text-purple-300"
        >
          <Trophy className="w-4 h-4 mr-1" />
          Badges
        </Button>
      </div>

      {/* Streaks Grid */}
      <div className="grid grid-cols-4 gap-2 mb-4">
        {/* Protein Streak */}
        <div className="bg-red-500/10 rounded-lg p-3 text-center border border-red-500/20">
          <p className="text-2xl mb-1">🥩</p>
          <p className={`text-xl font-bold ${getStreakColor(stats.streaks.protein.current)}`}>
            {stats.streaks.protein.current} {getStreakFlame(stats.streaks.protein.current)}
          </p>
          <p className="text-xs text-stone-400">Protein</p>
        </div>

        {/* Calories Streak */}
        <div className="bg-green-500/10 rounded-lg p-3 text-center border border-green-500/20">
          <p className="text-2xl mb-1">🎯</p>
          <p className={`text-xl font-bold ${getStreakColor(stats.streaks.calories.current)}`}>
            {stats.streaks.calories.current} {getStreakFlame(stats.streaks.calories.current)}
          </p>
          <p className="text-xs text-stone-400">Calories</p>
        </div>

        {/* Water Streak */}
        <div className="bg-blue-500/10 rounded-lg p-3 text-center border border-blue-500/20">
          <p className="text-2xl mb-1">💧</p>
          <p className={`text-xl font-bold ${getStreakColor(stats.streaks.water.current)}`}>
            {stats.streaks.water.current} {getStreakFlame(stats.streaks.water.current)}
          </p>
          <p className="text-xs text-stone-400">Water</p>
        </div>

        {/* Logging Streak */}
        <div className="bg-purple-500/10 rounded-lg p-3 text-center border border-purple-500/20">
          <p className="text-2xl mb-1">📝</p>
          <p className={`text-xl font-bold ${getStreakColor(stats.streaks.logging.current)}`}>
            {stats.streaks.logging.current} {getStreakFlame(stats.streaks.logging.current)}
          </p>
          <p className="text-xs text-stone-400">Logged</p>
        </div>
      </div>

      {/* Weekly Calendar View */}
      <div className="bg-stone-900/50 rounded-lg p-3 mb-3">
        <p className="text-xs text-stone-500 mb-2 uppercase tracking-wider">This Week</p>
        <div className="grid grid-cols-7 gap-1">
          {stats.weekly_summary.days.slice(0, 7).reverse().map((day, idx) => {
            const dayName = new Date(day.date).toLocaleDateString('en-US', { weekday: 'short' }).charAt(0);
            const allGoalsHit = day.hit_protein && day.hit_calories && day.hit_water;
            const someGoalsHit = day.hit_protein || day.hit_calories || day.hit_water || day.logged;
            
            return (
              <div key={day.date} className="text-center">
                <p className="text-xs text-stone-500 mb-1">{dayName}</p>
                <div 
                  className={`w-8 h-8 mx-auto rounded-full flex items-center justify-center ${
                    allGoalsHit 
                      ? 'bg-green-500 text-white' 
                      : someGoalsHit 
                        ? 'bg-yellow-500/50 text-yellow-200'
                        : day.logged
                          ? 'bg-stone-700 text-stone-300'
                          : 'bg-stone-800 text-stone-500'
                  }`}
                >
                  {allGoalsHit ? (
                    <Check className="w-4 h-4" />
                  ) : someGoalsHit ? (
                    <span className="text-xs">{[day.hit_protein, day.hit_calories, day.hit_water].filter(Boolean).length}</span>
                  ) : (
                    <X className="w-3 h-3" />
                  )}
                </div>
              </div>
            );
          })}
        </div>
        <div className="flex justify-center gap-4 mt-2 text-xs text-stone-500">
          <span className="flex items-center gap-1">
            <span className="w-3 h-3 rounded-full bg-green-500"></span> All goals
          </span>
          <span className="flex items-center gap-1">
            <span className="w-3 h-3 rounded-full bg-yellow-500/50"></span> Some goals
          </span>
        </div>
      </div>

      {/* Weekly Summary Stats */}
      <div className="grid grid-cols-4 gap-2 text-center">
        <div>
          <p className="text-lg font-bold text-red-400">{stats.weekly_summary.days_hit_protein}/7</p>
          <p className="text-xs text-stone-500">Protein</p>
        </div>
        <div>
          <p className="text-lg font-bold text-green-400">{stats.weekly_summary.days_hit_calories}/7</p>
          <p className="text-xs text-stone-500">Calories</p>
        </div>
        <div>
          <p className="text-lg font-bold text-blue-400">{stats.weekly_summary.days_hit_water}/7</p>
          <p className="text-xs text-stone-500">Water</p>
        </div>
        <div>
          <p className="text-lg font-bold text-purple-400">{stats.weekly_summary.days_logged}/7</p>
          <p className="text-xs text-stone-500">Logged</p>
        </div>
      </div>

      {/* Unlocked Achievements Preview */}
      {stats.achievements.length > 0 && (
        <div className="mt-4 pt-3 border-t border-stone-700">
          <div className="flex items-center justify-between">
            <p className="text-sm text-stone-400">Recent Badges</p>
            <Button
              size="sm"
              variant="ghost"
              onClick={loadAchievements}
              className="text-xs text-purple-400 p-0"
            >
              See all <ChevronRight className="w-3 h-3" />
            </Button>
          </div>
          <div className="flex gap-2 mt-2">
            {stats.achievements.slice(0, 4).map((achievement) => (
              <div
                key={achievement.id}
                className="bg-gradient-to-br from-yellow-500/20 to-orange-500/20 rounded-lg p-2 text-center border border-yellow-500/30"
                title={achievement.description}
              >
                <span className="text-2xl">{achievement.icon}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Achievements Modal */}
      {showAchievements && (
        <div 
          className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
          onClick={() => setShowAchievements(false)}
        >
          <Card 
            className="bg-stone-900 border-purple-500/30 p-6 max-w-md w-full max-h-[80vh] overflow-y-auto"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                <Trophy className="w-5 h-5 text-yellow-400" />
                Achievements
              </h3>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => setShowAchievements(false)}
                className="text-stone-400"
              >
                ✕
              </Button>
            </div>

            {/* Unlocked Count */}
            <div className="bg-gradient-to-r from-yellow-500/20 to-orange-500/20 rounded-lg p-3 mb-4 text-center">
              <p className="text-2xl font-bold text-yellow-400">
                {allAchievements.filter(a => a.unlocked).length} / {allAchievements.length}
              </p>
              <p className="text-sm text-stone-400">Badges Unlocked</p>
            </div>

            {/* Achievement Categories */}
            {['protein', 'water', 'calories', 'logging', 'special'].map(category => {
              const categoryAchievements = allAchievements.filter(a => a.category === category);
              if (categoryAchievements.length === 0) return null;
              
              const categoryNames = {
                protein: '🥩 Protein',
                water: '💧 Water',
                calories: '🎯 Calories',
                logging: '📝 Logging',
                special: '⭐ Special'
              };
              
              return (
                <div key={category} className="mb-4">
                  <p className="text-sm font-semibold text-stone-400 mb-2">{categoryNames[category]}</p>
                  <div className="space-y-2">
                    {categoryAchievements.map(achievement => (
                      <div 
                        key={achievement.id}
                        className={`flex items-center gap-3 p-3 rounded-lg ${
                          achievement.unlocked 
                            ? 'bg-gradient-to-r from-yellow-500/20 to-orange-500/20 border border-yellow-500/30' 
                            : 'bg-stone-800/50 opacity-50'
                        }`}
                      >
                        <span className="text-2xl">{achievement.icon}</span>
                        <div className="flex-1">
                          <p className={`font-medium ${achievement.unlocked ? 'text-white' : 'text-stone-500'}`}>
                            {achievement.name}
                          </p>
                          <p className="text-xs text-stone-400">{achievement.description}</p>
                        </div>
                        {achievement.unlocked && (
                          <Check className="w-5 h-5 text-green-400" />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </Card>
        </div>
      )}
    </Card>
  );
};

export default NutritionHabits;
